import { contentTransformAtom, isOverviewScaleAtom } from '@user/pages/ViewModelPage/contentTransformAtom';
import { useAtomValue, useSetAtom } from 'jotai/react';
import type { ForwardedRef } from 'react';
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';
import type { ScaleExtent } from './domain/ScaleExtent';
import type { ZoomTransform } from 'd3';
import { useUserOperationModes } from '@user/pages/ViewModelPage/UserOperationModesProvider';
import { useD3RightClickCallback } from '@view-model/models/common/hooks/useD3RightClickCallback';
import type { ViewBox } from '@view-model/domain/view-model';
import type { Point } from '@view-model/models/common/basic';
import { useZoomPanHandler } from './hook/useZoomPanHandler';
import { useRightClickMenuOpenState } from '@user/pages/ViewModelPage/useRightClickMenuOpenState';
import { isOperationCalibrationEnabled, recordCalibrationEvent } from '@framework/utils/operationCalibration';

type Props = {
  children: React.ReactNode;
  scaleExtent: ScaleExtent;
  viewBox: ViewBox;
  viewModelSVGRef: React.RefObject<SVGSVGElement | null>;
};

// 親コンポーネントから呼ばれる関数
export type SVGZoomPanHandlerRef = {
  resetZoom: (centerPosition: Point, scale: number, duration?: number) => void;
  zoom: (scale: number) => void;
};

const SVGZoomPanHandlerFn = (props: Props, forwardedRef: ForwardedRef<SVGZoomPanHandlerRef>) => {
  const { children, scaleExtent, viewBox, viewModelSVGRef } = props;
  const { userOperationMode } = useUserOperationModes();
  const setContentTransform = useSetAtom(contentTransformAtom);
  const isOverview = useAtomValue(isOverviewScaleAtom);
  const { activeRightClickMenuIds } = useRightClickMenuOpenState();

  const zoomPanHandlerRef = useRef<SVGGElement>(null);
  const previousTransformRef = useRef<ZoomTransform | null>(null);

  // ここではjotaiの変数に新しいtransformを設定するだけ
  // 別コンポーネントからこの値を参照し、svg要素にtransformを設定する
  const onZoom = useCallback(
    (t: ZoomTransform) => {
      if (activeRightClickMenuIds.length > 0) return; // 右クリックメニューが開いているときはズーム操作を無効にする
      if (isOperationCalibrationEnabled()) {
        const previous = previousTransformRef.current;
        if (previous) {
          const deltaX = t.x - previous.x;
          const deltaY = t.y - previous.y;
          if (t.k === previous.k && (deltaX !== 0 || deltaY !== 0)) {
            recordCalibrationEvent('viewport.pan', { distancePx: Math.hypot(deltaX, deltaY) });
          }
          if (t.k !== previous.k) {
            recordCalibrationEvent('viewport.zoom', {
              scaleRatio: t.k / previous.k,
              centerShiftPx: Math.hypot(deltaX, deltaY),
            });
          }
        }
        previousTransformRef.current = t;
      }
      setContentTransform((previous) =>
        previous.x === t.x && previous.y === t.y && previous.k === t.k ? previous : t
      );
    },
    [activeRightClickMenuIds, setContentTransform]
  );

  // 操作モードが「スライドモード」の時は右クリックメニュー（contextmenu）を無効にする
  useD3RightClickCallback(zoomPanHandlerRef, () => {}, userOperationMode === 'slide');

  // マウス操作のZoom/Panningに関してはこのコンポーネントでハンドリングするが、画面ボタン等によるZoom操作を可能
  // とするため、forwardRefで一部の機能をコンポーネントの外から呼び出せるように公開する
  const { resetZoom, zoom } = useZoomPanHandler({
    zoomPanHandlerRef,
    userOperationMode,
    scaleExtent,
    viewBox,
    onZoom,
    enabled: activeRightClickMenuIds.length === 0,
  });
  useImperativeHandle(forwardedRef, () => ({
    resetZoom,
    zoom,
  }));

  return (
    <svg
      ref={viewModelSVGRef}
      // VRT でキャンバスの描画位置が安定するのを待つために参照する (e2e の waitForCanvasStable)
      data-testid="view-model-canvas"
      // 引いて文字が読めない間は、.lod-detail の描画を CSS で省く（index.css）
      data-lod={isOverview ? 'overview' : undefined}
      pointerEvents="all"
      width={viewBox.width}
      height={viewBox.height}
      viewBox={viewBox.toSVGAttribute()}
    >
      <g ref={zoomPanHandlerRef}>{children}</g>
    </svg>
  );
};

// TODO: React 19になったらforwardRefは不要（非推奨）
export const SVGZoomPanHandler = forwardRef<SVGZoomPanHandlerRef, Props>(SVGZoomPanHandlerFn);
